import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { endpoints, asset } from "@/lib/api";
import { CTASection } from "@/components/site/Bits";
import { Reveal, LineReveal, ClippedImage } from "@/lib/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function EventTypeDetail() {
  const { slug } = useParams();
  const [et, setEt] = useState(null);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    let active = true;
    endpoints.eventType(slug).then((d) => {
      if (!active) return;
      setEt(d);
      document.title = d.seo_title || `${d.name} — ARIA`;
      const tag = (d.gallery_tags && d.gallery_tags[0]) || d.name;
      endpoints.media({ tag, limit: 12 }).then((m) => active && setGallery(m)).catch(() => {});
    }).catch(() => {});
    return () => { active = false; };
  }, [slug]);

  if (!et) return <div className="h-screen" />;

  return (
    <div data-testid="event-type-detail">
      {/* Hero */}
      <section className="relative h-[85svh] w-full overflow-hidden aria-grain">
        <img src={asset(et.hero_image)} alt={et.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-aria-ink/30" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-aria-ink/90 via-aria-ink/30 to-transparent" />
        <div className="relative z-10 flex h-full items-end">
          <div className="aria-container pb-20">
            <div className="mb-6 text-[0.72rem] uppercase tracking-[0.28em] text-aria-ivory/80">Occasions</div>
            <h1 className="font-display text-aria-ivory text-5xl sm:text-7xl md:text-8xl leading-[0.92] tracking-tight">
              <LineReveal lines={[et.name]} />
            </h1>
            <p className="mt-8 max-w-xl font-light text-lg text-aria-ivory/80">{et.tagline}</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="aria-container py-24 md:py-36">
        <Reveal>
          <p className="max-w-3xl font-display text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-aria-ink text-balance">
            {et.intro}
          </p>
        </Reveal>
      </section>

      {/* Benefits */}
      {et.benefits?.length > 0 && (
        <section className="bg-aria-sand py-24 md:py-36" data-testid="event-benefits">
          <div className="aria-container">
            <div className="eyebrow mb-14">Why ARIA</div>
            <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
              {et.benefits.map((b, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="border-t border-aria-travertine pt-8">
                    <span className="font-accent italic text-4xl text-aria-brass/60">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mt-6 font-display text-2xl md:text-3xl text-aria-ink">{b.title}</h3>
                    <p className="mt-4 font-light leading-relaxed text-aria-cocoa/75">{b.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="aria-container py-24 md:py-36" data-testid="event-gallery">
          <div className="mb-12 flex items-end justify-between">
            <h2 className="font-display text-4xl md:text-5xl text-aria-ink">A closer look</h2>
            <Link to="/gallery" className="aria-link text-[0.72rem] uppercase tracking-[0.2em] text-aria-ink/70">Full gallery</Link>
          </div>
          <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 [column-fill:_balance]">
            {gallery.map((m) => (
              <div key={m.id} className="mb-6 break-inside-avoid overflow-hidden">
                <img src={asset(m.url)} alt={m.alt} loading="lazy" className="w-full object-cover transition-transform duration-700 hover:scale-[1.03]" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Planner tips */}
      {et.planner_tips?.length > 0 && (
        <section className="bg-aria-ink text-aria-ivory py-24 md:py-36" data-testid="event-tips">
          <div className="aria-container grid grid-cols-1 gap-14 md:grid-cols-12">
            <div className="md:col-span-4">
              <div className="eyebrow mb-6">For the Planner</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight text-aria-ivory">Notes from the field</h2>
            </div>
            <div className="md:col-span-8">
              <ul className="divide-y divide-aria-ivory/10">
                {et.planner_tips.map((t, i) => (
                  <Reveal key={i}>
                    <li className="flex gap-6 py-6">
                      <span className="font-accent italic text-2xl text-aria-brass">{String(i + 1).padStart(2, "0")}</span>
                      <p className="font-light text-lg leading-relaxed text-aria-ivory/80">{t}</p>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {et.testimonial && (
        <section className="aria-container py-24 md:py-36">
          <Reveal>
            <blockquote className="mx-auto max-w-4xl text-center">
              <p className="font-display text-3xl md:text-5xl leading-[1.15] text-aria-ink text-balance">“{et.testimonial.quote}”</p>
              <footer className="mt-8 text-[0.72rem] uppercase tracking-[0.24em] text-aria-muted">
                {et.testimonial.author} — {et.testimonial.role}
              </footer>
            </blockquote>
          </Reveal>
        </section>
      )}

      {/* FAQs */}
      {et.faqs?.length > 0 && (
        <section className="bg-aria-sand py-24 md:py-36" data-testid="event-faqs">
          <div className="aria-container grid grid-cols-1 gap-12 md:grid-cols-12">
            <div className="md:col-span-4">
              <h2 className="font-display text-4xl md:text-5xl text-aria-ink">Questions</h2>
            </div>
            <div className="md:col-span-8">
              <Accordion type="single" collapsible className="w-full">
                {et.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-b border-aria-travertine">
                    <AccordionTrigger className="py-6 text-left font-display text-2xl text-aria-ink hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="pb-6 font-light text-base leading-relaxed text-aria-cocoa/80">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      <CTASection eyebrow="Enquire" title={[`Host your ${et.name.toLowerCase()}`, "with ARIA."]} />
    </div>
  );
}
